import {
  CareOrderStatusEnum,
  cleanupInitialFilterDateRange,
  FilterClauseType,
  filterExistsAndExcludes,
  getTypedKeys,
  IconName,
  removeEmptyFilters,
  useSidesFilter,
  useSymbolsFilter,
  type BlotterTableClientLocalFilter,
  type DateRangeFilter,
  type FilterableProperty,
  type FilterClause,
  type Leaves,
  type UseFilterBuilderProps,
} from '@talos/kyoko';
import { compact, isArray, isEqual, startCase, values } from 'lodash-es';
import { startTransition, useCallback, useMemo, useState, type SetStateAction } from 'react';
import { passesCareOrderDateRangeFilter } from './careOrderDateRangeFilter';
import type { CareOrderTimestampField } from './careOrderTimestampFields';
import { isCareOrderRow, type CareOrderBlotterEntity } from './types';

export { careOrderTimestampFields } from './careOrderTimestampFields';
export type { CareOrderTimestampField } from './careOrderTimestampFields';

export interface CareOrderFilter extends DateRangeFilter<CareOrderTimestampField> {
  Statuses?: string[];
  Symbols?: string[];
  Groups?: string[];
  Sides?: string;
  OriginationTrader?: string;
}

interface UseCareOrderFilterParams {
  initialFilter?: CareOrderFilter;
  saveFilter: (newFilter: any) => void;
  includeOpenCareOrdersOutsideDateRange?: boolean;
}

export function useCareOrderFilter({
  initialFilter,
  saveFilter,
  includeOpenCareOrdersOutsideDateRange = true,
}: UseCareOrderFilterParams) {
  const [filter, setFilter] = useState(() => cleanupInitialFilterDateRange(initialFilter));

  const changeFilter = useCallback(
    (action: SetStateAction<CareOrderFilter>) => {
      startTransition(() => {
        const priorFilter = filter;
        const newFilter = action instanceof Function ? action(filter) : action;
        if (!isEqual(priorFilter, newFilter)) {
          setFilter(newFilter);
          saveFilter(newFilter);
        }
      });
    },
    [filter, saveFilter]
  );

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<CareOrderBlotterEntity>>(
    row => {
      const data = row.data;
      if (!data) {
        return false;
      }
      if (!isCareOrderRow(data)) {
        // Only filter top level rows (care orders)
        return false;
      }
      if (!passesCareOrderDateRangeFilter(data, filter, includeOpenCareOrdersOutsideDateRange)) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Symbols', data, 'Symbol')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Statuses', data, 'status')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Sides', data, 'Side')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Groups', data, 'Group')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'OriginationTrader', data, 'originationTrader')) {
        return false;
      }

      return true;
    },
    [filter, includeOpenCareOrdersOutsideDateRange]
  );

  const sidesFilter = useSidesFilter();
  const symbolsFilter = useSymbolsFilter();

  const filterableProperties: FilterableProperty[] = useMemo(
    () => [
      sidesFilter,

      {
        key: 'Statuses',
        label: 'Status',
        icon: IconName.CheckCircle,
        options: values(CareOrderStatusEnum),
        getOptionLabel: startCase,
      },
      {
        key: 'OriginationTrader',
        label: 'Origination Trader',
        icon: IconName.User,
        control: 'text',
      },
      symbolsFilter,
    ],
    [sidesFilter, symbolsFilter]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      getTypedKeys(filter).forEach(key => {
        clauses.push({
          key: key,
          type: FilterClauseType.INCLUSIVE,
          selections: (isArray(filter[key]) ? filter[key] : compact([filter[key]])) as string[],
        });
      });
    }

    return clauses;
  }, [filter]);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter: CareOrderFilter = removeEmptyFilters<CareOrderFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies CareOrderFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const filterBuilderProps = useMemo(
    () =>
      ({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }) satisfies UseFilterBuilderProps,
    [initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );

  return {
    filter,
    filterableProperties,
    initialFilterClauses,
    changeFilter,
    clientSideFilter,
    filterBuilderProps,
  };
}

const colIDToFilterBuilderKeyMap = {
  status: 'Statuses',
  Symbol: 'Symbols',
  Group: 'Groups',
  Side: 'Sides',
} satisfies Partial<Record<Leaves<CareOrderBlotterEntity>, keyof CareOrderFilter>>;

export function colIDToFilterBuilderKey(id: string): keyof CareOrderFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap];
}
